import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';

import { getHeadingId } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';
import { getOrParseContentBodyAdf } from '@confluence/adf-analysis-core/entry-points/getOrParseContentBodyAdf.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { TableOfContentsInlineNode, TableOfContentsItem } from './tableOfContentsTypes.ts';

function isHeadingLevel(level: unknown): level is TableOfContentsItem['level'] {
	return typeof level === 'number' && level >= 1 && level <= 6;
}

function formatDate(timestamp: unknown): string {
	const millis = Number(timestamp);
	if (!Number.isFinite(millis)) {
		return '';
	}

	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	}).format(new Date(millis));
}

function getInlineNodeText(node: ADFEntity): string {
	if (node.type === 'date') {
		return formatDate(node.attrs?.timestamp);
	}

	return node.text ?? node.attrs?.text ?? node.attrs?.shortName ?? '';
}

function getInlineContent(heading: ADFEntity): TableOfContentsInlineNode[] {
	return (heading.content ?? []).flatMap((node): TableOfContentsInlineNode[] => {
		if (!node) {
			return [];
		}

		if (node.type === 'emoji') {
			return [
				{
					type: 'emoji',
					shortName: node.attrs?.shortName ?? '',
					id: node.attrs?.id,
					text: node.attrs?.text,
				},
			];
		}

		const text = getInlineNodeText(node);
		return text ? [{ type: 'text', text }] : [];
	});
}

function createTableOfContentsItem(
	heading: ADFEntity,
	headingId: string,
	position?: number,
): TableOfContentsItem | undefined {
	const level = heading.attrs?.level;
	if (!isHeadingLevel(level)) {
		return undefined;
	}

	const content = getInlineContent(heading);
	const text =
		content
			.map((segment) =>
				segment.type === 'emoji' ? segment.text || segment.shortName : segment.text,
			)
			.join('') || headingId;

	return {
		text,
		content,
		headingId,
		level,
		...(position === undefined ? {} : { position }),
	};
}

export function getTableOfContentsItemsFromAdf(
	value: string | null | undefined,
	contentId?: string,
): TableOfContentsItem[] {
	if (!value) {
		return [];
	}

	try {
		let document: ADFEntity;
		if (contentId && expValEquals('cc-proj-xl-use-shared-adf-parsing', 'isEnabled', true)) {
			const result = getOrParseContentBodyAdf(contentId, value);
			if (!result.ok) {
				return [];
			}
			document = result.doc;
		} else {
			document = JSON.parse(value) as ADFEntity;
		}
		const headingIds = new Set<string>();
		const items: TableOfContentsItem[] = [];

		traverse(document, {
			heading: (heading) => {
				const headingId = getHeadingId(heading, headingIds);
				if (headingId) {
					const item = createTableOfContentsItem(heading, headingId);
					if (item) {
						items.push(item);
					}
				}
				// Keep traverse from rebuilding content on the shared heading node.
				return heading;
			},
		});

		return items;
	} catch {
		return [];
	}
}

export function getEditorTableOfContentsItemsFromDoc(doc: ProseMirrorNode): TableOfContentsItem[] {
	const headingIds = new Set<string>();
	const items: TableOfContentsItem[] = [];

	doc.descendants((node, position) => {
		if (node.type.name !== 'heading') {
			return true;
		}

		const heading = node.toJSON() as ADFEntity;
		const headingId = getHeadingId(heading, headingIds);
		if (headingId) {
			const item = createTableOfContentsItem(heading, headingId, position);
			if (item) {
				items.push(item);
			}
		}

		return false;
	});

	return items;
}
